import React from "react";

import { Button } from "@hdir/ui/button";
import { Chip } from "@hdir/ui/chip";
import { Link } from "@hdir/ui/link";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from "@hdir/ui/accordion";
import { Stepper, StepperStep } from "@hdir/ui/stepper";
import {
  faCalendarDays, faClock, faCoins, faPrint
} from "@fortawesome/pro-light-svg-icons";

import Richtext from "../../../react4xp/shared/Richtext/Richtext";
import FontAwesomeIconWrapper from "../../../react4xp/shared/FontAwesomeIconWrapper/FontAwesomeIconWrapper";

import { ShowTilskuddProps } from "./type.d";

export default function ShowTilskudd(props: Readonly<ShowTilskuddProps>) {
  const {
    amount,
    applyLink,
    applyLinkText,
    encodedComponent,
    deadlineDate,
    deadlineHour,
    eligibleAppliersList,
    grantRegulations,
    intro,
    isExpired,
    localizedForShowTilskudd,
    pdfUrl,
    reportLinkText,
    pdfServiceUrl,
    title,
    timelineData,
    linkStandardTermsAndCondtions
  } = props;

  function currentActiveIndex(): number {
    let activeStepIndex: number = -1;
    timelineData.forEach((step, index) => {
      if (step.activeStep) {
        activeStepIndex = index;
      }
    });

    return activeStepIndex;
  }

  function isCompleted(stepIndex: number, currentActiveIndex: number): boolean {
    // No current active index, e.g all steps are completed
    if (currentActiveIndex === -1) return true;
    return stepIndex < currentActiveIndex;
  }

  return (
    <div className="mt-xxxl py-design-component-mobile tablet:py-design-component-tablet desktop:py-design-component-desktop">
      { title && (
        <h1 className="max-w-text-width">{title}</h1>
      )}
      { intro && (
        <p className="mt-lg max-w-text-width">{intro}</p>
      )}
      <div className="flex mt-xl flex-wrap gap-md">
        {isExpired ? (
          deadlineDate && (
            <Chip className="!bg-[#FCF3F9] !text-[#7C145C] !border-[#7C145C] !leading-6 !text-md" size="large">
              <FontAwesomeIconWrapper className="mr-xs" icon={faCalendarDays} color="#7C145C" />
              {localizedForShowTilskudd.deadline}
              {" "}
              {deadlineDate}
            </Chip>
          )
        ) : (
          deadlineDate && (
            <Chip className="!bg-surface-feedback-success !border-feedback-success !text-icon-feedback-success !leading-6 !text-md" size="large">
              <FontAwesomeIconWrapper className="mr-xs mobile:mr-0" icon={faCalendarDays} />
              <span className="hidden tablet:inline-flex">{localizedForShowTilskudd.deadline}</span>
              {" "}
              {deadlineDate}
              {deadlineHour && (
                <>
                  <FontAwesomeIconWrapper className="mx-xs mobile:mr-0" icon={faClock} />
                  <span className="hidden tablet:inline-flex">{localizedForShowTilskudd.deadlineHourText}</span>
                  {" "}
                  {deadlineHour}
                </>
              )}
            </Chip>
          )
        )}

        { amount && (
          <Chip className="!bg-surface-neutral-subtle !text-action !border-action-primary !leading-6 !text-md" size="large">
            <FontAwesomeIconWrapper className="mr-xs" icon={faCoins} />
            {localizedForShowTilskudd.amount}
            {" "}
            {amount}
            {" "}
            kr
          </Chip>
        )}
      </div>

      { eligibleAppliersList?.length > 0 && (
        <div className="mt-xxxl max-w-text-width">
          <h2>Hvem kan søke tilskudd?</h2>
          <ul className="mt-md">
            { eligibleAppliersList.map((elem) => (
              <li key={elem}>{elem}</li>
            ))}
          </ul>
        </div>
      )}

      { timelineData?.length > 1 && (
        <div className="mt-xxxl hidden tablet:block">
          <h2>Slik blir prosessen</h2>
          <Stepper className="mt-lg" activeStepIndex={currentActiveIndex()} isStepCompleted={(stepIndex) => isCompleted(stepIndex, currentActiveIndex())} checkmarkWhenCompleted>
            {timelineData.map((step) => <StepperStep key={step.id} label={step.text} />)}
          </Stepper>
        </div>
      )}

      {(applyLinkText || applyLink) && (
        <div className="mt-xxxl">
          <h2>Søk om tilskudd</h2>

          {applyLinkText && (
            <Richtext
              className="mt-md richTextRegulations max-w-text-width"
              text={applyLinkText}
            />
          )}

          {isExpired ? (
            <>
              <Button
                disabled
                className="mt-md !text-lg !py-md !px-lg"
                variant="primary"
                size="large"
              >
                {localizedForShowTilskudd.applyButton}
              </Button>
              <p className="mt-md text-feedback-error">
                {localizedForShowTilskudd.deadlineExpiredHelpText}
              </p>
            </>
          ) : (
            applyLink && (
              <Link
                className="mt-md !text-lg !py-md !px-lg"
                href={applyLink}
                variant="button-primary"
              >
                {localizedForShowTilskudd.applyButton}
              </Link>
            )
          )}
        </div>
      )}

      { reportLinkText && (
        <div className="mt-xxxl">
          <h2>Rapporter på tilskudd</h2>
          <Richtext className="mt-md richTextRegulations max-w-text-width" text={reportLinkText} />
        </div>
      )}

      { grantRegulations?.length > 0 && (
        <div className="mt-xxxl max-w-text-width" id="regulations">
          <h2>Regelverk for ordningen</h2>
          <div className="mt-xl">
            <Accordion>
              { grantRegulations.map((regulation) => (
                <AccordionItem className="box-border rounded-sm" value={regulation.title} key={`${regulation.title}`}>
                  <AccordionTrigger className="text-xl py-xs">{regulation.title}</AccordionTrigger>
                  <AccordionContent className="mt-0">
                    <Richtext className="richTextRegulations" text={regulation.text} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="flex nt-flex-col tablet:nt-flex-row mt-sm">
            <Button
              variant="tertiary"
              id="exportTilskuddRegulationsPdf"
              className="!pl-0"
              data-pdfvalues='{"pdfTemplateMode":"single"}'
              data-getpdfservice={pdfServiceUrl}
              data-title={`${title} - Regelverk`}
              data-pageurl={`${pdfUrl}?printRegulations=true&showTilskuddComponent=${encodedComponent}`}
            >
              <FontAwesomeIconWrapper icon={faPrint} />
              {localizedForShowTilskudd.printPdfRegulations}
            </Button>

            { linkStandardTermsAndCondtions && (
              <>
                <span className="mt-[8px] h-6 w-[1px] bg-[#212121] hidden tablet:block" />
                <Link className="mt-[-1px] tablet:pl-[16px]" href={linkStandardTermsAndCondtions}>
                  {localizedForShowTilskudd.linkTextStandardTermsAndCondtions}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>

  );
}
